import { ContentUnderTopAppBar, TopAppBar } from '@llt-frontend/react-material-top-app-bar';

import DayOrchestrator from '../components/day-orchestrator';
import { Drawer } from '@llt-frontend/react-material-drawer';
import EssayStatusNotification from '../components/study-support/async-essay/status-notification';
import { InteractionEventTrigger } from '@blueshift-ui/telemetry';
import MEDIA_QUERIES from '../constants/media-queries';
import NOTIFICATION_TYPES from '../constants/notification-types';
import PageLoadingIndicator from '../components/loading-indicators/page';
import PropTypes from 'prop-types';
import React from 'react';
import SideNavigation from '../components/side-navigation';
import UserMenu from '../components/user-menu';
import isEmployee from '../utilities/is-employee';
import logoSvg from '@llt-frontend/brands/varsity-tutors/e.svg';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import useUserContext from '../hooks/context/use-user-context';

//

const notificationComponents = {
  [NOTIFICATION_TYPES.dayOrchestrator]: DayOrchestrator,
  [NOTIFICATION_TYPES.asyncEssay]: EssayStatusNotification,
};

function _Header({ onCreateStudent, onSwitchStudent, ...forwardedProps }) {
  const { activeUser } = useUserContext();

  //
  // Event Handlers

  function handleAddStudent() {
    onCreateStudent && onCreateStudent();
  }

  function handleSwitchStudent() {
    onSwitchStudent && onSwitchStudent();
  }

  //
  // Render

  return (
    <TopAppBar
      {...forwardedProps}
      actions={
        !isEmployee(activeUser?.type)
          ? [
              <UserMenu
                key="user-menu"
                onAddStudent={handleAddStudent}
                onSwitchStudent={handleSwitchStudent}
              />,
            ]
          : []
      }
      className="site-header"
    >
      <InteractionEventTrigger
        analytics={{
          action: 'vt-home-click',
          category: 'site-header',
        }}
        element="a"
        elementProps={{
          href: '/learning-lab',
          title: 'Varsity Tutors homepage',
        }}
      >
        <img alt="" className="mdc-top-app-bar__site-logo" src={logoSvg} />
      </InteractionEventTrigger>

      <span className="site-header__app-name">Learning Lab</span>
    </TopAppBar>
  );
}

function _Main({ children }) {
  return (
    <ContentUnderTopAppBar className="main" component="main" role="main">
      {children}
      <PageLoadingIndicator />
    </ContentUnderTopAppBar>
  );
}

function _MainContent({ children, notifications }) {
  return (
    <div className="main-content" id="main-content">
      {notifications?.map((notification, index) => {
        const NotificationComponent = notificationComponents[notification.type];
        return <NotificationComponent {...notification.options} key={index} />;
      })}
      {children}
    </div>
  );
}

//

function PrimaryLayout({ children, notifications, onCreateStudent, onSwitchStudent }) {
  const showFixedNav = useMediaQuery(MEDIA_QUERIES.showFixedNav);
  const { t: translate, ready: translationLoaded } = useTranslation('layouts/primary-layout');

  //
  // Render

  if (!translationLoaded) {
    return null;
  }

  if (showFixedNav) {
    return (
      <>
        <_Header {...{ onCreateStudent, onSwitchStudent }} />

        <_Main>
          <Drawer aria-label={translate('landmarkLabel')} className="side-navigation">
            <SideNavigation />
          </Drawer>

          <_MainContent {...{ notifications }}>{children}</_MainContent>
        </_Main>
      </>
    );
  }

  return (
    <>
      <Drawer modal topAppBar={<_Header {...{ onCreateStudent, onSwitchStudent }} />}>
        <SideNavigation />
      </Drawer>

      <_Main>
        <_MainContent {...{ notifications }}>{children}</_MainContent>
      </_Main>
    </>
  );
}

PrimaryLayout.propTypes = {
  children: PropTypes.node.isRequired,
  dayOrchestrator: PropTypes.object,
  onCreateStudent: PropTypes.func,
  onSwitchStudent: PropTypes.func,
};

// Exports
// -------

export default PrimaryLayout;
